import type { ReactNode } from "react";
import type { PortfolioPeriod } from "@richrich/contracts";
import { cn } from "../lib/cn";

/** Ordered portfolio periods (mirrors `PortfolioPeriodSchema`). */
export const PORTFOLIO_PERIODS: readonly PortfolioPeriod[] = [
  "1d",
  "7d",
  "30d",
  "90d",
  "1y",
  "all",
];

const PERIOD_LABEL: Record<PortfolioPeriod, string> = {
  "1d": "1D",
  "7d": "1W",
  "30d": "1M",
  "90d": "3M",
  "1y": "1Y",
  all: "All",
};

export interface PeriodSwitchProps {
  value: PortfolioPeriod;
  onChange: (period: PortfolioPeriod) => void;
  /** Restrict / reorder the offered periods. */
  periods?: readonly PortfolioPeriod[];
  className?: string;
}

/**
 * Segmented control for the portfolio time window. Typed to the contract enum so
 * it composes with TanStack Router's typed `?period=` search param.
 */
export function PeriodSwitch({
  value,
  onChange,
  periods = PORTFOLIO_PERIODS,
  className,
}: PeriodSwitchProps): ReactNode {
  return (
    <div
      role="tablist"
      aria-label="Time period"
      className={cn(
        "inline-flex items-center gap-0.5 rounded-lg border border-hairline bg-page p-0.5",
        className,
      )}
    >
      {periods.map((period) => {
        const active = period === value;
        return (
          <button
            key={period}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(period)}
            className={cn(
              "rounded-md px-2 py-1 text-xs font-medium tabular-nums transition-colors",
              active ? "bg-surface text-ink shadow-sm" : "text-muted hover:text-ink-secondary",
            )}
          >
            {PERIOD_LABEL[period]}
          </button>
        );
      })}
    </div>
  );
}
